import { Moment } from 'moment-timezone';
import { Marketplace } from 'types/Marketplace';

export interface BusinessHours {
    open: number;
    close: number;
}

const normalizeTimeValue = (time?: string | number | null): number | null => {
    if (time === null || time === undefined || time === '') {
        return null;
    }

    if (typeof time === 'number') {
        if (!Number.isFinite(time) || time <= 0) {
            return null;
        }

        return time < 24 ? time * 100 : time;
    }

    const normalized = time.replace(':', '').trim();

    if (normalized.length === 0) {
        return null;
    }

    const value = parseInt(normalized, 10);

    if (!Number.isFinite(value) || value <= 0) {
        return null;
    }

    return normalized.length <= 2 ? value * 100 : value;
};

export const timeNumberToMinutes = (time: number) => {
    const hours = Math.floor(time / 100);
    const minutes = time % 100;

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null;
    }

    return hours * 60 + minutes;
};

export const buildBusinessHours = (
    open?: string | number | null,
    close?: string | number | null
): BusinessHours | null => {
    const openValue = normalizeTimeValue(open);
    const closeValue = normalizeTimeValue(close);

    if (openValue === null || closeValue === null) {
        return null;
    }

    const openMinutes = timeNumberToMinutes(openValue);
    const closeMinutes = timeNumberToMinutes(closeValue);

    if (openMinutes === null || closeMinutes === null || openMinutes >= closeMinutes) {
        return null;
    }

    return {
        open: openValue,
        close: closeValue
    };
};

const getMarketplaceScheduleKey = (date: Moment) => {
    return date.format('dddd').toLowerCase() as keyof Marketplace['schedule'];
};

export const getMarketplaceBusinessHoursForDate = (
    marketplace: Marketplace,
    date: Moment
): BusinessHours | null => {
    const specialHour = marketplace.specialHours.find(
        specialHour => date.format('YYYY-MM-DD') === specialHour.date
    );

    if (specialHour) {
        if (specialHour.closed) {
            return null;
        }

        return buildBusinessHours(specialHour.businessHours.open, specialHour.businessHours.close);
    }

    const scheduleRange = marketplace.schedule[getMarketplaceScheduleKey(date)];

    if (!scheduleRange) {
        return null;
    }

    return buildBusinessHours(scheduleRange.open, scheduleRange.close);
};
